import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../shared/PageHeader";
import PageState from "../shared/PageState";
import AdminPanel from "../shared/AdminPanel";
import styles from "../styles/OffersSection.module.css";

function safeJsonStringify(value) {
  if (value == null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return "";
  }
}

function safeJsonParse(value) {
  if (!value || !String(value).trim()) return null;
  try {
    return JSON.parse(String(value));
  } catch (_) {
    return null;
  }
}

function normalizeText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseListText(value) {
  const raw = String(value || "");
  const parts = raw
    .split(/[\n,]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
}

function joinListText(list) {
  return (Array.isArray(list) ? list : []).filter(Boolean).join("\n");
}

const INTENT_OPTIONS = [
  { value: "exam_preparation", label: "Подготовка к экзаменам" },
  { value: "education", label: "Образование" },
  { value: "programming", label: "Программирование" },
  { value: "technical_help", label: "Техподдержка" },
  { value: "business", label: "Бизнес" },
  { value: "shopping", label: "Покупки" },
  { value: "entertainment", label: "Развлечения" },
  { value: "gaming", label: "Игры" }
];

function getIntentLabel(intentValue) {
  const key = String(intentValue || "").trim().toLowerCase();
  const hit = INTENT_OPTIONS.find((x) => x.value === key);
  return hit ? hit.label : (key || "unknown");
}

function formatIntentList(list, { withCodes = true } = {}) {
  const arr = Array.isArray(list) ? list : [];
  const out = [];
  for (const v of arr) {
    const code = String(v || "").trim().toLowerCase();
    if (!code) continue;
    const label = getIntentLabel(code);
    out.push(withCodes ? `${label} (${code})` : label);
  }
  return Array.from(new Set(out));
}

const TAG_PRESETS = [
  {
    id: "education",
    title: "Образование (школа/экзамены)",
    tags: {
      allowedIntents: ["education", "exam_preparation"],
      blockedIntents: ["programming", "technical_help"],
      keywords: ["егэ", "огэ", "экзамен", "подготовка", "школа", "математика", "русский"],
      excludeKeywords: ["код", "python", "backend", "frontend", "nodejs", "react"],
      minSimilarity: 0.42,
      matchingText: "ЕГЭ ОГЭ школа экзамены подготовка"
    }
  },
  {
    id: "saas",
    title: "SaaS / B2B (бизнес)",
    tags: {
      allowedIntents: ["business"],
      blockedIntents: ["programming", "technical_help"],
      keywords: ["crm", "лиды", "воронка", "продажи", "маркетинг", "конверсия"],
      excludeKeywords: ["код", "react", "nodejs"],
      minSimilarity: 0.45
    }
  },
  {
    id: "ai_tools",
    title: "AI-инструменты",
    tags: {
      allowedIntents: ["education", "business", "technical_help"],
      keywords: ["нейросеть", "ии", "gpt", "чатгпт", "генерация", "промпт"],
      minSimilarity: 0.4
    }
  },
  {
    id: "programming_courses",
    title: "Курс по программированию",
    tags: {
      allowedIntents: ["programming", "technical_help"],
      blockedIntents: ["exam_preparation"],
      keywords: ["python", "javascript", "nodejs", "react", "backend", "frontend", "api", "git"],
      excludeKeywords: ["огэ", "егэ"],
      minSimilarity: 0.45
    }
  },
  {
    id: "shopping",
    title: "Покупки",
    tags: {
      allowedIntents: ["shopping"],
      keywords: ["купить", "заказать", "цена", "доставка", "сравни"],
      minSimilarity: 0.45
    }
  },
  {
    id: "entertainment",
    title: "Развлечения",
    tags: {
      allowedIntents: ["entertainment", "gaming"],
      keywords: ["фильм", "сериал", "музыка", "игра", "аниме"],
      minSimilarity: 0.4
    }
  }
];

function buildTargetTagsFromForm(form) {
  const tags = {};
  const keywords = parseListText(form.keywordsText).map((x) => x.toLowerCase());
  const excludeKeywords = parseListText(form.excludeKeywordsText).map((x) => x.toLowerCase());
  const allowedIntents = Array.from(form.allowedIntents || []).map((x) => String(x).toLowerCase());
  const blockedIntents = Array.from(form.blockedIntents || []).map((x) => String(x).toLowerCase());
  const categories = Array.from(form.categories || []).map((x) => String(x).toLowerCase());
  const matchingText = normalizeText(form.matchingText);
  const redirectUrl = normalizeText(form.redirectUrl);

  const minSimilarityNum = Number(form.minSimilarity);
  const minSimilarity = Number.isFinite(minSimilarityNum) ? Math.max(0, Math.min(1, minSimilarityNum)) : null;

  if (keywords.length) tags.keywords = keywords;
  if (excludeKeywords.length) tags.excludeKeywords = excludeKeywords;
  if (allowedIntents.length) tags.allowedIntents = Array.from(new Set(allowedIntents));
  if (blockedIntents.length) tags.blockedIntents = Array.from(new Set(blockedIntents));
  if (categories.length) tags.categories = Array.from(new Set(categories));
  if (matchingText) tags.matchingText = matchingText;
  if (minSimilarity != null) tags.minSimilarity = minSimilarity;
  if (redirectUrl) tags.redirectUrl = redirectUrl;
  return tags;
}

function getQualityWarnings({ title, full_prompt, target_tags }) {
  const warnings = [];
  if (!String(title || "").trim()) warnings.push("Название оффера обязательно.");
  if (!String(full_prompt || "").trim()) warnings.push("Текст оффера (full_prompt) обязателен.");

  const tags = target_tags && typeof target_tags === "object" ? target_tags : {};
  const keywords = Array.isArray(tags.keywords) ? tags.keywords : [];
  const allowed = Array.isArray(tags.allowedIntents) ? tags.allowedIntents : [];
  const categories = Array.isArray(tags.categories) ? tags.categories : [];
  const blocked = Array.isArray(tags.blockedIntents) ? tags.blockedIntents : [];

  if (!keywords.length && !allowed.length && !categories.length) {
    warnings.push("Нет keywords/allowedIntents/categories: оффер может быть слишком широким и шумным.");
  }
  if ((allowed.includes("education") || allowed.includes("exam_preparation")) && !blocked.length) {
    warnings.push("Не задан blockedIntents: возможны показы в «Программирование»/«Техподдержка» (programming/technical_help).");
  }
  const minSim = typeof tags.minSimilarity === "number" ? tags.minSimilarity : null;
  if (minSim != null && minSim > 0.85) warnings.push("minSimilarity слишком высокий: оффер может почти никогда не появляться.");
  const overlap = keywords.filter((k) => Array.isArray(tags.excludeKeywords) && tags.excludeKeywords.includes(k));
  if (overlap.length) warnings.push(`Ключевые слова пересекаются со стоп-словами (excludeKeywords): ${overlap.slice(0, 8).join(", ")}`);

  return warnings;
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${Math.round(n * 1000) / 10}%`;
}

function clamp01(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function similarityLabel(value) {
  const v = clamp01(value);
  if (v >= 0.6) return "Высокая";
  if (v >= 0.4) return "Средняя";
  if (v >= 0.25) return "Низкая";
  return "Очень низкая";
}

function OfferModal({ initial, mode, onClose, onSave, onPreviewSimilarity, onValidateOffer, onDebugRank, onDebugRuntime, onLoadTimeseries }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [shortDescription, setShortDescription] = useState(initial?.short_description || "");
  const [fullPrompt, setFullPrompt] = useState(initial?.full_prompt || "");
  const [priority, setPriority] = useState(String(initial?.priority ?? 0));
  const [cooldownHours, setCooldownHours] = useState(String(initial?.cooldown_hours ?? 48));
  const [maxImpressions, setMaxImpressions] = useState(String(initial?.max_impressions_per_user ?? 5));
  const [enabled, setEnabled] = useState(initial?.enabled !== false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [confirmWarnings, setConfirmWarnings] = useState(false);

  const initialTags = initial?.target_tags && typeof initial.target_tags === "object" ? initial.target_tags : {};
  const [presetId, setPresetId] = useState("");
  const [advancedTagsOpen, setAdvancedTagsOpen] = useState(false);
  const [advancedTagsRaw, setAdvancedTagsRaw] = useState(safeJsonStringify(initialTags));

  const [keywordsText, setKeywordsText] = useState(joinListText(initialTags.keywords));
  const [excludeKeywordsText, setExcludeKeywordsText] = useState(joinListText(initialTags.excludeKeywords || initialTags.exclude_keywords));
  const [allowedIntents, setAllowedIntents] = useState(
    new Set(Array.isArray(initialTags.allowedIntents || initialTags.allowed_intents) ? (initialTags.allowedIntents || initialTags.allowed_intents) : [])
  );
  const [blockedIntents, setBlockedIntents] = useState(
    new Set(Array.isArray(initialTags.blockedIntents || initialTags.blocked_intents) ? (initialTags.blockedIntents || initialTags.blocked_intents) : [])
  );
  const [categories, setCategories] = useState(new Set(Array.isArray(initialTags.categories) ? initialTags.categories : []));
  const [matchingText, setMatchingText] = useState(String(initialTags.matchingText || initialTags.matching_text || ""));
  const [minSimilarity, setMinSimilarity] = useState(
    initialTags.minSimilarity != null ? String(initialTags.minSimilarity) : initialTags.min_similarity != null ? String(initialTags.min_similarity) : ""
  );
  const [redirectUrl, setRedirectUrl] = useState(String(initialTags.redirectUrl || ""));

  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewValue, setPreviewValue] = useState(null);
  const [previewError, setPreviewError] = useState("");

  const [liveValidation, setLiveValidation] = useState(null);
  const [liveValidationError, setLiveValidationError] = useState("");
  const [liveValidating, setLiveValidating] = useState(false);

  const [debugText, setDebugText] = useState("");
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState("");
  const [debugValue, setDebugValue] = useState(null);

  const [runtimeUserId, setRuntimeUserId] = useState("");
  const [runtimeText, setRuntimeText] = useState("");
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeError, setRuntimeError] = useState("");
  const [runtimeValue, setRuntimeValue] = useState(null);

  const [seriesDays, setSeriesDays] = useState(30);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState("");
  const [seriesItems, setSeriesItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!onLoadTimeseries || mode !== "edit" || !initial?.id) return;
      setSeriesLoading(true);
      setSeriesError("");
      try {
        const items = await onLoadTimeseries(initial.id, seriesDays);
        if (!cancelled) setSeriesItems(Array.isArray(items) ? items : []);
      } catch (e) {
        if (!cancelled) setSeriesError(e?.response?.data?.error || e?.message || "Failed to load timeseries");
      } finally {
        if (!cancelled) setSeriesLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [initial?.id, mode, onLoadTimeseries, seriesDays]);

  const builtTags = useMemo(() => {
    const form = {
      keywordsText,
      excludeKeywordsText,
      allowedIntents,
      blockedIntents,
      categories,
      matchingText,
      minSimilarity,
      redirectUrl
    };
    return buildTargetTagsFromForm(form);
  }, [allowedIntents, blockedIntents, categories, excludeKeywordsText, keywordsText, matchingText, minSimilarity, redirectUrl]);

  const effectiveTargetTags = useMemo(() => {
    if (!advancedTagsOpen) return builtTags;
    const parsed = safeJsonParse(advancedTagsRaw);
    return parsed && typeof parsed === "object" ? parsed : null;
  }, [advancedTagsOpen, advancedTagsRaw, builtTags]);

  const advancedValid = useMemo(
    () => !advancedTagsOpen || advancedTagsRaw.trim() === "" || safeJsonParse(advancedTagsRaw) !== null,
    [advancedTagsOpen, advancedTagsRaw]
  );

  const explanation = useMemo(() => {
    const tags = effectiveTargetTags && typeof effectiveTargetTags === "object" ? effectiveTargetTags : {};
    const kw = Array.isArray(tags.keywords) ? tags.keywords : [];
    const ex = Array.isArray(tags.excludeKeywords) ? tags.excludeKeywords : [];
    const allow = Array.isArray(tags.allowedIntents) ? tags.allowedIntents : [];
    const block = Array.isArray(tags.blockedIntents) ? tags.blockedIntents : [];
    const lines = [];
    lines.push("Когда оффер вероятнее появится:");
    if (kw.length) lines.push(`• Ключевые слова: ${kw.slice(0, 12).join(", ")}${kw.length > 12 ? "…" : ""}`);
    if (allow.length) lines.push(`• Intent: ${formatIntentList(allow).join(", ")}`);
    if (!kw.length && !allow.length) lines.push("• нет явного таргетинга (может быть шумно)");
    lines.push("Когда оффер будет блокироваться:");
    if (block.length) lines.push(`• Заблокированные intent: ${formatIntentList(block).join(", ")}`);
    if (ex.length) lines.push(`• Стоп-слова: ${ex.slice(0, 12).join(", ")}${ex.length > 12 ? "…" : ""}`);
    if (!block.length && !ex.length) lines.push("• блокировок нет (риск нерелевантных показов)");
    if (tags.minSimilarity != null) lines.push(`Порог: minSimilarity=${String(tags.minSimilarity)}`);
    return lines;
  }, [effectiveTargetTags]);

  useEffect(() => {
    let cancelled = false;
    if (typeof onValidateOffer !== "function") return undefined;

    const run = async () => {
      setLiveValidating(true);
      setLiveValidationError("");
      try {
        const res = await onValidateOffer({
          title: title.trim(),
          full_prompt: fullPrompt.trim(),
          target_tags: effectiveTargetTags
        });
        if (cancelled) return;
        setLiveValidation(res || null);
      } catch (e) {
        if (cancelled) return;
        setLiveValidation(null);
        setLiveValidationError(e?.response?.data?.error || e?.message || "Не удалось проверить настройки");
      } finally {
        if (!cancelled) setLiveValidating(false);
      }
    };

    const t = setTimeout(run, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [effectiveTargetTags, fullPrompt, onValidateOffer, title]);

  async function handleSave() {
    setError("");
    setWarnings([]);
    setConfirmWarnings(false);
    if (!title.trim()) return setError("Название оффера обязательно");
    if (!fullPrompt.trim()) return setError("Текст оффера (full_prompt) обязателен");
    if (advancedTagsOpen && !advancedValid) return setError("target_tags в режиме Advanced должен быть валидным JSON");
    if (advancedTagsOpen && advancedTagsRaw.trim() && effectiveTargetTags == null) return setError("target_tags JSON не распознан");

    const payload = {
      title: title.trim(),
      short_description: shortDescription.trim() ? shortDescription : null,
      full_prompt: fullPrompt.trim(),
      target_tags: effectiveTargetTags,
      priority: Number(priority || 0) || 0,
      cooldown_hours: Math.max(0, Number(cooldownHours || 0) || 0),
      max_impressions_per_user: Math.max(1, Number(maxImpressions || 1) || 1),
      enabled
    };

    if (liveValidation && liveValidation.ok === false) {
      const first = Array.isArray(liveValidation.errors) && liveValidation.errors.length ? liveValidation.errors[0] : null;
      setError(first?.message || "Offer is invalid");
      return;
    }

    const localWarnings = getQualityWarnings(payload);
    const liveWarnings = Array.isArray(liveValidation?.warnings) ? liveValidation.warnings.map((w) => w?.message || String(w)) : [];
    const allWarnings = [...localWarnings, ...liveWarnings].filter(Boolean);
    if (allWarnings.length && !confirmWarnings) {
      setWarnings(allWarnings);
      setConfirmWarnings(true);
      return;
    }

    setSaving(true);
    try {
      const result = await onSave(payload);
      const serverWarnings = Array.isArray(result?.warnings) ? result.warnings : [];
      if (serverWarnings.length) {
        setWarnings(serverWarnings.map((w) => w?.message || String(w)));
        setConfirmWarnings(true);
        return;
      }
      onClose();
    } catch (e) {
      console.error("offer save error:", e);
      setError(e?.response?.data?.error || e?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  async function handlePreview() {
    setPreviewError("");
    if (!initial?.id) {
      setPreviewError("Сначала сохрани оффер, затем можно проверить similarity.");
      return;
    }
    if (!previewText.trim()) {
      setPreviewError("Введи пример сообщения пользователя.");
      return;
    }
    setPreviewLoading(true);
    try {
      const result = await onPreviewSimilarity(initial.id, previewText.trim());
      setPreviewValue(result);
    } catch (e) {
      console.error("preview similarity error:", e);
      setPreviewValue(null);
      setPreviewError(e?.response?.data?.error || e?.message || "Не удалось посчитать similarity");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleDebugRank() {
    setDebugError("");
    setDebugValue(null);
    if (typeof onDebugRank !== "function") return;
    if (!debugText.trim()) {
      setDebugError("Введи пример сообщения пользователя.");
      return;
    }
    setDebugLoading(true);
    try {
      const res = await onDebugRank({ text: debugText.trim(), limit: 10, maxOffers: 250 });
      setDebugValue(res);
    } catch (e) {
      console.error("debug rank error:", e);
      setDebugError(e?.response?.data?.error || e?.message || "Не удалось выполнить debug-rank");
    } finally {
      setDebugLoading(false);
    }
  }

  async function handleDebugRuntime() {
    setRuntimeError("");
    setRuntimeValue(null);
    if (typeof onDebugRuntime !== "function") return;
    const uid = Number(runtimeUserId || 0) || 0;
    if (!uid) {
      setRuntimeError("Укажи userId.");
      return;
    }
    if (!runtimeText.trim()) {
      setRuntimeError("Введи пример сообщения пользователя.");
      return;
    }
    setRuntimeLoading(true);
    try {
      const res = await onDebugRuntime({ userId: uid, text: runtimeText.trim(), limit: 3 });
      setRuntimeValue(res);
    } catch (e) {
      console.error("debug runtime error:", e);
      setRuntimeError(e?.response?.data?.error || e?.message || "Не удалось выполнить проверку как в рантайме");
    } finally {
      setRuntimeLoading(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalEyebrow}>AI Offers</div>
            <h3 className={styles.modalTitle}>{mode === "create" ? "Создание оффера" : "Редактирование оффера"}</h3>
          </div>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Закрыть
          </button>
        </div>

        {error ? <div className={styles.errorBox}>{error}</div> : null}
        {liveValidationError ? <div className={styles.errorBox}>{liveValidationError}</div> : null}
        {warnings.length ? (
          <div className={styles.warningBox}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Предупреждения</div>
            {warnings.map((w, idx) => (
              <div key={`warn-${idx}`}>• {w}</div>
            ))}
            {confirmWarnings ? (
              <div className={styles.hint}>Нажми «Сохранить» ещё раз, чтобы сохранить несмотря на предупреждения.</div>
            ) : null}
          </div>
        ) : null}

        <div className={styles.formGrid}>
          <div className={styles.sectionTitle}>Основное</div>
          <label className={styles.field}>
            <span>Название</span>
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например: Подписка Pro" />
          </label>

          <label className={styles.field}>
            <span>Статус</span>
            <label className={styles.inlineToggle}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>{enabled ? "Включён" : "Выключен"}</span>
            </label>
          </label>

          <label className={styles.fieldFull}>
            <span>Короткое описание (для админки)</span>
            <textarea
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              rows={2}
              placeholder="1 строка: что это и кому полезно"
            />
          </label>

          <label className={styles.fieldFull}>
            <span>Текст оффера (используется в рекомендациях)</span>
            <textarea
              value={fullPrompt}
              onChange={(e) => setFullPrompt(e.target.value)}
              rows={6}
              placeholder="Опиши оффер так, чтобы GPT мог нативно порекомендовать его в 1–2 предложениях."
            />
            <div className={styles.hint}>
              Совет: указывай выгоду и кому подходит. Не пиши агрессивную рекламу.
            </div>
          </label>

          <div className={styles.sectionTitle}>Таргетинг рекомендаций</div>

          <label className={styles.fieldFull}>
            <span>Шаблон (пресет)</span>
            <select
              value={presetId}
              onChange={(e) => {
                const id = e.target.value;
                setPresetId(id);
                const preset = TAG_PRESETS.find((p) => p.id === id);
                if (!preset) return;
                const t = preset.tags || {};
                setKeywordsText(joinListText(t.keywords));
                setExcludeKeywordsText(joinListText(t.excludeKeywords));
                setAllowedIntents(new Set(t.allowedIntents || []));
                setBlockedIntents(new Set(t.blockedIntents || []));
                setCategories(new Set(t.categories || []));
                setMatchingText(String(t.matchingText || ""));
                setMinSimilarity(t.minSimilarity != null ? String(t.minSimilarity) : "");
                setRedirectUrl(String(t.redirectUrl || ""));
                setAdvancedTagsRaw(safeJsonStringify(t));
              }}
            >
              <option value="">— без шаблона —</option>
              {TAG_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
            <div className={styles.hint}>Шаблон заполняет таргетинг. После выбора можно вручную поправить поля.</div>
          </label>

          <label className={styles.fieldFull}>
            <span>Ключевые слова (keywords)</span>
            <textarea
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              rows={3}
              placeholder={"Слова/темы, которые должны встречаться в сообщении пользователя. По одному на строку.\nПример:\nегэ\nэкзамен\nподготовка"}
            />
            <div className={styles.hint}>Если совпало keyword (или intent/category), оффер попадает в кандидаты. Делай keywords конкретными.</div>
          </label>

          <label className={styles.fieldFull}>
            <span>Стоп-слова (excludeKeywords)</span>
            <textarea
              value={excludeKeywordsText}
              onChange={(e) => setExcludeKeywordsText(e.target.value)}
              rows={3}
              placeholder={"Слова, которые БЛОКИРУЮТ оффер. По одному на строку.\nПример:\npython\nbackend\nreact"}
            />
            <div className={styles.hint}>Если в сообщении есть excludeKeyword, оффер будет отклонён.</div>
          </label>

          <div className={styles.intentGrid}>
            <div className={styles.intentCol}>
              <div className={styles.intentTitle}>Разрешённые intent (allowedIntents)</div>
              <div className={styles.hint}>Мягкое правило: повышает релевантность, если intent совпал.</div>
              {INTENT_OPTIONS.map((opt) => (
                <label key={`allow-${opt.value}`} className={styles.intentRow}>
                  <input
                    type="checkbox"
                    checked={allowedIntents.has(opt.value)}
                    onChange={(e) => {
                      setAllowedIntents((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(opt.value);
                        else next.delete(opt.value);
                        return next;
                      });
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <div className={styles.intentCol}>
              <div className={styles.intentTitle}>Запрещённые intent (blockedIntents)</div>
              <div className={styles.hint}>Жёсткий блок: оффер никогда не будет выбран при этих intent.</div>
              {INTENT_OPTIONS.map((opt) => (
                <label key={`block-${opt.value}`} className={styles.intentRow}>
                  <input
                    type="checkbox"
                    checked={blockedIntents.has(opt.value)}
                    onChange={(e) => {
                      setBlockedIntents((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(opt.value);
                        else next.delete(opt.value);
                        return next;
                      });
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <label className={styles.fieldFull}>
            <span>categories (дополнительно)</span>
            <div className={styles.hint}>Сейчас categories в ранжировании ведут себя почти как intents. Обычно можно оставить пустым.</div>
            <div className={styles.categoriesRow}>
              {INTENT_OPTIONS.map((opt) => (
                <label key={`cat-${opt.value}`} className={styles.pill}>
                  <input
                    type="checkbox"
                    checked={categories.has(opt.value)}
                    onChange={(e) => {
                      setCategories((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(opt.value);
                        else next.delete(opt.value);
                        return next;
                      });
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </label>

          <label className={styles.fieldFull}>
            <span>matchingText (подсказка для embeddings)</span>
            <input
              value={matchingText}
              onChange={(e) => setMatchingText(e.target.value)}
              placeholder="Доп. текст для embeddings: ЕГЭ ОГЭ экзамен подготовка..."
            />
            <div className={styles.hint}>Помогает similarity на коротких сообщениях. Пиши ключевые фразы/темы, без воды.</div>
          </label>

          <div className={styles.targetGrid}>
            <label className={styles.field}>
              <span>minSimilarity (порог для оффера)</span>
              <input value={minSimilarity} onChange={(e) => setMinSimilarity(e.target.value)} placeholder="0.42" />
              <div className={styles.hint}>В рантайме используется `max(global, offer.minSimilarity)`.</div>
            </label>
            <label className={styles.field}>
              <span>redirectUrl (ссылка при клике)</span>
              <input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)} placeholder="https://vk.com/..." />
              <div className={styles.hint}>Если указано, модель может использовать `click_url` (трек кликов/CTR).</div>
            </label>
          </div>

          <div className={styles.previewBox}>
            <div className={styles.previewTitle}>Live explanation</div>
            <div className={styles.previewHint}>
              {liveValidating ? "Проверяю настройки..." : "Коротко: когда оффер покажется, а когда будет заблокирован."}
            </div>
            <div className={styles.previewResult}>
              {explanation.map((line, idx) => (
                <div key={`exp-${idx}`}>{line}</div>
              ))}
            </div>
            {Array.isArray(liveValidation?.warnings) && liveValidation.warnings.length ? (
              <div className={styles.similarityHint}>
                {liveValidation.warnings.slice(0, 8).map((w, idx) => (
                  <div key={`lvw-${idx}`}>• {w?.message || String(w)}</div>
                ))}
              </div>
            ) : null}
          </div>

          <label className={styles.fieldFull}>
            <span className={styles.inlineToggle}>
              <input type="checkbox" checked={advancedTagsOpen} onChange={(e) => setAdvancedTagsOpen(e.target.checked)} />
              <span>Advanced: редактировать JSON вручную</span>
            </span>
            {advancedTagsOpen ? (
              <>
                <textarea
                  value={advancedTagsRaw}
                  onChange={(e) => setAdvancedTagsRaw(e.target.value)}
                  rows={6}
                  className={!advancedValid ? styles.invalid : undefined}
                  placeholder='{"keywords":["егэ","экзамен"],"allowedIntents":["exam_preparation"]}'
                />
                <div className={styles.hint}>В режиме Advanced поля выше игнорируются — сохраняется только этот JSON.</div>
              </>
            ) : (
              <div className={styles.hint}>
                Превью JSON (генерируется автоматически):
                <pre className={styles.jsonPreview}>{safeJsonStringify(builtTags)}</pre>
              </div>
            )}
          </label>

          <label className={styles.field}>
            <span>Приоритет</span>
            <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
            <div className={styles.hint}>Диапазон: от -100 до 100 (влияет на ранжирование).</div>
          </label>

          <label className={styles.field}>
            <span>Кулдаун на пользователя (часы)</span>
            <input type="number" value={cooldownHours} onChange={(e) => setCooldownHours(e.target.value)} />
            <small style={{ opacity: 0.75 }}>Считается отдельно для каждого user_id, не глобально</small>
          </label>

          <label className={styles.field}>
            <span>Лимит показов на пользователя</span>
            <input type="number" value={maxImpressions} onChange={(e) => setMaxImpressions(e.target.value)} />
          </label>
        </div>

        {mode === "edit" ? (
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>Проверка релевантности (similarity)</div>
                <div className={styles.previewHint}>Вставь пример сообщения пользователя и посмотри близость к офферу.</div>
              </div>
              <button type="button" className={styles.primaryButton} onClick={handlePreview} disabled={previewLoading}>
                {previewLoading ? "Считаю..." : "Посчитать"}
              </button>
            </div>
            <textarea
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  handlePreview();
                }
              }}
              rows={3}
              placeholder="Пример: Я готовлюсь к ЕГЭ по математике, помоги составить план..."
            />
            {previewError ? <div className={styles.errorBox}>{previewError}</div> : null}
            {previewValue != null ? (
              <div className={styles.previewResult}>
                {previewValue.selected === true ? (
                  <div className={styles.successBox}>ВЫБРАНО</div>
                ) : previewValue.blockedReason ? (
                  <div className={styles.errorBox}>
                    Отклонено: {String(previewValue.blockedReason)}
                  </div>
                ) : null}
                <div className={styles.similarityHeader}>
                  <div>
                    <div className={styles.similarityValue}>
                      Similarity: {Math.round((Number(previewValue.similarity || 0) || 0) * 10000) / 10000}
                    </div>
                    <div className={styles.similarityLabel}>
                      {similarityLabel(previewValue.similarity)} релевантность
                      {previewValue.intent ? ` · intent: ${getIntentLabel(previewValue.intent)} (${previewValue.intent})` : ""}
                    </div>
                    <div className={styles.hint}>
                      Итоговый скор: {Math.round((Number(previewValue.finalScore || 0) || 0) * 10000) / 10000}
                      {previewValue.intentConfidence != null
                        ? ` · уверенность intent: ${Math.round(clamp01(previewValue.intentConfidence) * 100)}%`
                        : ""}
                    </div>
                    {previewValue.thresholds ? (
                      <div className={styles.hint}>
                        Пороги: sim {previewValue.thresholds.effectiveMinSimilarity} (global {previewValue.thresholds.globalMinSimilarity}
                        {previewValue.thresholds.offerMinSimilarity != null ? `, offer ${previewValue.thresholds.offerMinSimilarity}` : ""})
                        {" · "}minFinalScore {previewValue.thresholds.minFinalScore}
                      </div>
                    ) : null}
                    {previewValue.components ? (
                      <div className={styles.hint}>
                        Компоненты: sim {previewValue.components.similarityScore} · intent {previewValue.components.intentScore} · business {previewValue.components.businessScore} · final {previewValue.finalScore}
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.similarityBadge}>{Math.round(clamp01(previewValue.similarity) * 100)}%</div>
                </div>
                <div className={styles.similarityBar}>
                  <div
                    className={styles.similarityFill}
                    style={{ width: `${Math.round(clamp01(previewValue.similarity) * 100)}%` }}
                  />
                </div>
                {Array.isArray(previewValue.reasons) && previewValue.reasons.length ? (
                  <div className={styles.similarityHint}>
                    {previewValue.reasons.map((r, idx) => (
                      <div key={`reason-${idx}`}>• {r}</div>
                    ))}
                  </div>
                ) : null}
                {previewValue.embeddingTextPreview ? (
                  <div className={styles.hint} style={{ marginTop: 10 }}>
                    Текст для embeddings (что реально уходит в embeddings):
                    <pre className={styles.jsonPreview}>{String(previewValue.embeddingTextPreview)}</pre>
                  </div>
                ) : null}
                <div className={styles.similarityHint}>
                  Обычно имеет смысл показывать оффер при значении ~0.35–0.45+ (зависит от базы офферов).
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === "edit" ? (
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>Отладка ранжирования (все офферы)</div>
                <div className={styles.previewHint}>Показывает, какие офферы выбрались бы и почему другие отклонены.</div>
              </div>
              <button type="button" className={styles.secondaryButton} onClick={handleDebugRank} disabled={debugLoading}>
                {debugLoading ? "Считаю..." : "Запустить"}
              </button>
            </div>
            <textarea
              value={debugText}
              onChange={(e) => setDebugText(e.target.value)}
              rows={3}
              placeholder="Пример сообщения пользователя..."
            />
            {debugError ? <div className={styles.errorBox}>{debugError}</div> : null}
            {debugValue?.success ? (
              <div className={styles.previewResult}>
                <div className={styles.hint}>
                  intent: {getIntentLabel(debugValue?.intent?.intent)} ({debugValue?.intent?.intent || "unknown"}) · уверенность:{" "}
                  {Math.round(clamp01(debugValue?.intent?.confidence) * 100)}% · офферов: {debugValue?.offers} · с embeddings:{" "}
                  {debugValue?.offersWithEmbeddings}
                </div>
                {Array.isArray(debugValue.selected) && debugValue.selected.length ? (
                  <div className={styles.similarityHint}>
                    {debugValue.selected.map((row) => (
                      <div key={`sel-${row?.offer?.id}`}>
                        • #{row?.offer?.id} {row?.offer?.title} — sim {Math.round(clamp01(row?.similarity) * 1000) / 1000} · score{" "}
                        {Math.round(clamp01(row?.score) * 1000) / 1000}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.similarityHint}>Ничего не выбрано.</div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === "edit" ? (
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>Проверка как в рантайме (по userId)</div>
                <div className={styles.previewHint}>Берёт history пользователя из БД и прогоняет рекомендации как в боте.</div>
              </div>
              <button type="button" className={styles.secondaryButton} onClick={handleDebugRuntime} disabled={runtimeLoading}>
                {runtimeLoading ? "Считаю..." : "Проверить"}
              </button>
            </div>
            <div className={styles.targetGrid}>
              <label className={styles.field}>
                <span>userId</span>
                <input value={runtimeUserId} onChange={(e) => setRuntimeUserId(e.target.value)} placeholder="12345" />
              </label>
              <div />
            </div>
            <textarea
              value={runtimeText}
              onChange={(e) => setRuntimeText(e.target.value)}
              rows={3}
              placeholder="Сообщение пользователя (как в чате)..."
            />
            {runtimeError ? <div className={styles.errorBox}>{runtimeError}</div> : null}
                {runtimeValue?.success ? (
                  <div className={styles.previewResult}>
                    <div className={styles.hint}>
                      history сообщений: {runtimeValue.historyMessages}
                      {runtimeValue.debug?.intent ? ` · intent: ${getIntentLabel(runtimeValue.debug.intent)} (${runtimeValue.debug.intent})` : ""}
                      {runtimeValue.debug?.intentConfidence != null
                        ? ` · уверенность: ${Math.round(clamp01(runtimeValue.debug.intentConfidence) * 100)}%`
                        : ""}
                      {runtimeValue.debug?.userEmbeddingUpdated != null
                        ? ` · userEmbeddingUpdated: ${runtimeValue.debug.userEmbeddingUpdated ? "yes" : "no"}`
                        : ""}
                    </div>
                {Array.isArray(runtimeValue.recommendations) && runtimeValue.recommendations.length ? (
                  <div className={styles.similarityHint}>
                    {runtimeValue.recommendations.map((r) => (
                      <div key={`rt-${r.offerId}`}>
                        • #{r.offerId} {r.title} — sim {Math.round(clamp01(r.similarity) * 1000) / 1000} · score{" "}
                        {Math.round(clamp01(r.score) * 1000) / 1000}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.similarityHint}>Ничего не выбрано в рантайме.</div>
                )}
                {Array.isArray(runtimeValue.debug?.decisions) && runtimeValue.debug.decisions.length ? (
                  <div className={styles.hint} style={{ marginTop: 10 }}>
                    Decisions (первые 50):
                    <pre className={styles.jsonPreview}>{safeJsonStringify(runtimeValue.debug.decisions.slice(0, 50))}</pre>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === "edit" ? (
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>Аналитика</div>
                <div className={styles.previewHint}>Показы/клики/CTR по дням (ai_offer_impressions).</div>
              </div>
              <select value={seriesDays} onChange={(e) => setSeriesDays(Number(e.target.value) || 30)}>
                <option value={7}>7d</option>
                <option value={14}>14d</option>
                <option value={30}>30d</option>
                <option value={90}>90d</option>
              </select>
            </div>
            {seriesError ? <div className={styles.errorBox}>{seriesError}</div> : null}
            {seriesLoading ? <div className={styles.previewHint}>Загрузка...</div> : null}
            {!seriesLoading && !seriesItems.length ? <div className={styles.previewHint}>Пока нет данных.</div> : null}
            {!seriesLoading && seriesItems.length ? (
              <div className={styles.seriesGrid}>
                {(() => {
                  const maxImp = Math.max(1, ...seriesItems.map((x) => Number(x.impressions || 0) || 0));
                  return seriesItems.map((row) => {
                    const imp = Number(row.impressions || 0) || 0;
                    const clicks = Number(row.clicks || 0) || 0;
                    const ctr = Number(row.ctr || 0) || 0;
                    const width = Math.round((imp / maxImp) * 100);
                    return (
                      <div key={`day-${row.day}`} className={styles.seriesRow}>
                        <div className={styles.seriesDay}>{row.day}</div>
                        <div className={styles.seriesBar} title={`impressions=${imp} clicks=${clicks} ctr=${Math.round(ctr * 1000) / 10}%`}>
                          <div className={styles.seriesFill} style={{ width: `${width}%` }} />
                        </div>
                        <div className={styles.seriesMeta}>
                          {imp} imp ? {clicks} clk ? {Math.round(ctr * 1000) / 10}% ctr
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={styles.modalActions}>
          <button type="button" className={styles.primaryButton} onClick={handleSave} disabled={saving}>
            {saving ? "Сохраняю..." : "Сохранить"}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={saving}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

export function AiOffersPage({ offers }) {
  const { state, actions } = offers;
  const [selected, setSelected] = useState(() => new Set());
  const [modal, setModal] = useState(null); // {mode, offer}

  const allSelected = useMemo(() => state.items.length > 0 && selected.size === state.items.length, [selected, state.items.length]);

  const toggleSelectAll = (checked) => {
    if (!checked) return setSelected(new Set());
    const next = new Set();
    state.items.forEach((o) => next.add(o.id));
    setSelected(next);
  };

  const toggleSelectOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedIds = useMemo(() => Array.from(selected.values()), [selected]);

  const openCreate = () => setModal({ mode: "create", offer: null });
  const openEdit = (offer) => setModal({ mode: "edit", offer });

  async function handleDelete(id) {
    if (!window.confirm("Удалить оффер?")) return;
    await actions.deleteOffer(id);
    setSelected(new Set());
  }

  async function handleBulk(action) {
    if (!selectedIds.length) return;
    const confirmText =
      action === "delete"
        ? "Удалить выбранные офферы?"
        : action === "disable"
        ? "Выключить выбранные офферы?"
        : "Включить выбранные офферы?";
    if (!window.confirm(confirmText)) return;
    await actions.bulkAction(action, selectedIds);
    setSelected(new Set());
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="AI Офферы"
        description="Офферы для recommendation engine: эмбеддинги, кулдаун, лимиты и статистика."
        actions={
          <button type="button" className={styles.primaryButton} onClick={openCreate}>
            + Новый оффер
          </button>
        }
      />

      <PageState loading={state.loading} error={state.error} />

      <AdminPanel
        title="Фильтры и поиск"
        actions={
          <button type="button" className={styles.secondaryButton} onClick={actions.reload} disabled={state.loading}>
            Обновить
          </button>
        }
      >
        <div className={styles.filters}>
          <label className={styles.filterField}>
            <span>Поиск</span>
            <input
              value={state.query}
              onChange={(e) => actions.setQuery(e.target.value)}
              onKeyDown={(e) => (e.key === "Enter" ? actions.reload() : null)}
              placeholder="Название / описание / текст оффера"
            />
          </label>

          <label className={styles.filterField}>
            <span>Статус</span>
            <select value={state.enabledFilter} onChange={(e) => actions.setEnabledFilter(e.target.value)}>
              <option value="all">Все</option>
              <option value="enabled">Включённые</option>
              <option value="disabled">Выключенные</option>
            </select>
          </label>

          <label className={styles.filterField}>
            <span>Сортировка</span>
            <div className={styles.sortRow}>
              <select value={state.orderBy} onChange={(e) => actions.setOrderBy(e.target.value)}>
                <option value="updated_at">updated_at</option>
                <option value="created_at">created_at</option>
                <option value="priority">priority</option>
                <option value="title">title</option>
                <option value="id">id</option>
              </select>
              <select value={state.orderDir} onChange={(e) => actions.setOrderDir(e.target.value)}>
                <option value="DESC">DESC</option>
                <option value="ASC">ASC</option>
              </select>
            </div>
          </label>

          <label className={styles.filterField}>
            <span>На странице</span>
            <select
              value={state.limit}
              onChange={(e) => {
                actions.setLimit(Number(e.target.value));
                actions.setOffset(0);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>

          <div className={styles.filterActions}>
            <button type="button" className={styles.primaryButton} onClick={actions.reload}>
              Применить
            </button>
          </div>
        </div>
      </AdminPanel>

      <AdminPanel
        title="Офферы"
        actions={
          <div className={styles.bulkRow}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleBulk("enable")}
              disabled={!selectedIds.length}
            >
              Включить
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleBulk("disable")}
              disabled={!selectedIds.length}
            >
              Выключить
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={() => handleBulk("delete")}
              disabled={!selectedIds.length}
            >
              Удалить
            </button>
          </div>
        }
      >
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    aria-label="Select all"
                  />
                </th>
                <th>Название</th>
                <th>ID</th>
                <th>Статус</th>
                <th>Приоритет</th>
                <th>Показы</th>
                <th>Клики</th>
                <th>CTR</th>
                <th>Avg sim</th>
                <th>Обновлён</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.items.map((offer) => (
                <tr key={offer.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(offer.id)}
                      onChange={(e) => toggleSelectOne(offer.id, e.target.checked)}
                      aria-label={`Select offer ${offer.id}`}
                    />
                  </td>
                  <td>
                    <div className={styles.titleCell}>
                      <div className={styles.titleMain}>{offer.title}</div>
                      {offer.short_description ? <div className={styles.titleSub}>{offer.short_description}</div> : null}
                    </div>
                  </td>
                  <td>{offer.id}</td>
                  <td>{offer.enabled ? "✅" : "—"}</td>
                  <td>{offer.priority}</td>
                  <td>{offer.stats?.impressions ?? 0}</td>
                  <td>{offer.stats?.clicks ?? 0}</td>
                  <td>{formatPct(offer.stats?.ctr ?? 0)}</td>
                  <td>{Math.round((offer.stats?.avgSimilarity ?? 0) * 10000) / 10000}</td>
                  <td>{offer.updated_at ? String(offer.updated_at).slice(0, 19).replace("T", " ") : ""}</td>
                  <td className={styles.rowActions}>
                    <button type="button" className={styles.secondaryButton} onClick={() => openEdit(offer)}>
                      Редактировать
                    </button>
                    <button type="button" className={styles.dangerButton} onClick={() => handleDelete(offer.id)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
              {!state.items.length ? (
                <tr>
                  <td colSpan={11} className={styles.emptyCell}>
                    Нет офферов
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => actions.setPage(state.page - 1)}
            disabled={state.page <= 1}
          >
            ←
          </button>
          <div className={styles.paginationText}>
            Страница {state.page} / {state.totalPages} · Всего {state.total}
          </div>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => actions.setPage(state.page + 1)}
            disabled={state.page >= state.totalPages}
          >
            →
          </button>
        </div>
      </AdminPanel>

      {modal ? (
        <OfferModal
          mode={modal.mode}
          initial={modal.offer}
          onClose={() => setModal(null)}
          onSave={(payload) =>
            modal.mode === "create" ? actions.createOffer(payload) : actions.updateOffer(modal.offer.id, payload)
          }
          onPreviewSimilarity={actions.previewSimilarity}
          onValidateOffer={actions.validateOffer}
          onDebugRank={actions.debugRank}
          onDebugRuntime={actions.debugRuntime}
          onLoadTimeseries={actions.loadTimeseries}
        />
      ) : null}
    </div>
  );
}

export default AiOffersPage;
